const ABORT_ERROR_MESSAGES = [
  // Chromium
  'signal is aborted without reason',
  // Firefox
  'The user aborted a request',
  // Safari, and TRPC's own TRPCAbortError
  'The operation was aborted',
];

/**
 * Detects benign request cancellations: the user navigated away, a component
 * unmounted, a request was superseded, or `AbortController.abort()` was called.
 * Browsers throw a `DOMException` with name `'AbortError'` and a browser-specific
 * message. These are not failures and must not be logged as errors.
 *
 * The primary signal is `error.name === 'AbortError'`, which is standardized
 * across Chromium, Firefox, and Safari (and also set by TRPC's `TRPCAbortError`).
 * The message list is a fallback for wrapped or serialized errors that have lost
 * their `name`.
 */
export function isAbortError(error: unknown): boolean {
  if (error == null) {
    return false;
  }

  if (typeof error === 'object' && 'name' in error && (error as { name?: unknown }).name === 'AbortError') {
    return true;
  }

  const message =
    typeof error === 'string'
      ? error
      : typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
        ? (error as { message: string }).message
        : '';

  return ABORT_ERROR_MESSAGES.some((abortMessage) => message.includes(abortMessage));
}
